import { Suspense, lazy, useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { supabase } from "@/integrations/supabase/client";
import { DevHumorWidget } from "@/components/DevHumorWidget";
import { SocialProof } from "@/components/SocialProof";
import { AdPlaceholder } from "@/components/AdPlaceholder";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import heroBg from "@/assets/hero-bg.webp";
import { useHomepageData } from "@/hooks/useHomepageData";
import type { Post } from "@/lib/blog-data";

// Phase 3: Lazy load islands for progressive hydration
const FeaturedPostIsland = lazy(() => import("@/components/islands/FeaturedPostIsland").then(m => ({ default: m.FeaturedPostIsland })));
const RecentPostsIsland = lazy(() => import("@/components/islands/RecentPostsIsland").then(m => ({ default: m.RecentPostsIsland })));
const EditorPicksIsland = lazy(() => import("@/components/islands/EditorPicksIsland").then(m => ({ default: m.EditorPicksIsland })));

const Index = () => {
  // Phase 1 & 2: Parallel fetching with React Query caching
  const { data, isLoading } = useHomepageData();
  const [hasSubscription, setHasSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setCheckingSubscription(false);
        return;
      }

      const { data } = await supabase.rpc('has_active_subscription', {
        profile_id: user.id
      });

      setHasSubscription(data || false);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setCheckingSubscription(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col w-full max-w-full overflow-x-hidden">
      {/* Phase 3: Static shell - renders immediately */}
      <Header />
      
      <main className="flex-1 w-full max-w-full">
        {/* Static Hero Section - Phase 3: Above-fold, renders immediately */}
        <section className="relative overflow-hidden w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-accent/20 animate-gradient" />
          <div className="absolute inset-0 opacity-10">
            <img src={heroBg} alt="" width="1920" height="1080" fetchPriority="high" className="w-full h-full object-cover" />
          </div>
          <div className="container relative py-24 md:py-32 w-full">
            <div className="max-w-3xl mx-auto text-center space-y-8 animate-fade-in px-4">
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-tight break-words">
                That's <span className="gradient-text">sooo.dev</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground break-words">
                Where we turn your daily programming struggles into content that's more entertaining than your stack traces.
              </p>
              
              {/* Social Proof */}
              <div className="max-w-2xl mx-auto">
                <SocialProof />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 w-full">
                <Link to="/blog" className="w-full sm:w-auto">
                  <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-lg px-8 w-full sm:w-auto">
                    Start Reading <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/newsletter" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="text-lg px-8 w-full sm:w-auto">
                    Subscribe to Newsletter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Static Dev Humor Widget - Phase 3: Renders immediately */}
        <section className="container py-16 w-full max-w-full">
          <div className="max-w-2xl mx-auto px-4">
            <DevHumorWidget />
          </div>
        </section>

        {/* Phase 3: Dynamic islands - Progressive hydration with Suspense */}
        {!isLoading && data && (
          <>
            <Suspense fallback={<FeaturedPostSkeleton />}>
              <FeaturedPostIsland posts={data.featuredPosts as unknown as Post[]} />
            </Suspense>

            {/* Ad Banner */}
            <section className="container py-8 w-full max-w-full">
              <AdPlaceholder variant="banner" />
            </section>

            <Suspense fallback={<RecentPostsSkeleton />}>
              <RecentPostsIsland posts={data.recentPosts as unknown as Post[]} />
            </Suspense>

            {/* Conversion: Show upgrade prompt for non-premium users */}
            {!checkingSubscription && !hasSubscription && (
              <section className="container py-8 w-full max-w-full">
                <div className="max-w-4xl mx-auto px-4">
                  <UpgradePrompt 
                    feature="Get Personalized Recommendations"
                    description="AI-powered content feed based on your reading history, plus exclusive deep dives"
                    variant="banner"
                  />
                </div>
              </section>
            )}

            <Suspense fallback={<EditorPicksSkeleton />}>
              <EditorPicksIsland recommendations={data.recommendations as unknown as React.ComponentProps<typeof EditorPicksIsland>['recommendations']} />
            </Suspense>
          </>
        )}

        {/* Show skeletons during initial load */}
        {isLoading && (
          <>
            <FeaturedPostSkeleton />
            <RecentPostsSkeleton />
            <EditorPicksSkeleton />
          </>
        )}

        {/* Static Newsletter Section - Phase 3: Renders immediately */}
        <section className="container py-16 w-full max-w-full">
          <div className="rounded-2xl gradient-border p-8 md:p-12 text-center max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 break-words">Never Miss a Rant</h2>
            <p className="text-muted-foreground mb-6 break-words">
              Get the latest sarcastic takes on tech delivered straight to your inbox. Support the chaos for just $1/month.
            </p>
            <div className="flex justify-center w-full">
              <Link to="/newsletter" className="w-full sm:w-auto">
                <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 w-full sm:w-auto">
                  Subscribe for $1/month
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Static Footer */}
      <Footer />
    </div>
  );
};

// Skeleton components for loading states
const FeaturedPostSkeleton = () => (
  <section className="container py-16">
    <div className="flex items-center gap-2 mb-8">
      <div className="h-6 w-6 bg-muted rounded animate-pulse" />
      <div className="h-7 w-24 bg-muted rounded animate-pulse" />
    </div>
    <div className="animate-pulse">
      <div className="h-96 bg-muted rounded-xl"></div>
    </div>
  </section>
);

const RecentPostsSkeleton = () => (
  <section className="container py-16">
    <div className="flex items-center justify-between mb-8">
      <div className="h-9 w-32 bg-muted rounded animate-pulse" />
      <div className="h-10 w-28 bg-muted rounded animate-pulse" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="h-48 bg-muted rounded-t-xl"></div>
          <div className="p-6 space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-full"></div>
            <div className="h-3 bg-muted rounded w-5/6"></div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const EditorPicksSkeleton = () => (
  <section className="container py-16">
    <div className="flex items-center gap-2 mb-8">
      <div className="h-6 w-6 bg-muted rounded animate-pulse" />
      <div className="h-9 w-40 bg-muted rounded animate-pulse" />
    </div>
    <div className="h-4 w-96 bg-muted rounded animate-pulse mb-8" />
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse rounded-xl overflow-hidden" style={{ background: 'hsl(var(--card))' }}>
          <div className="h-48 bg-muted"></div>
          <div className="p-6 space-y-3">
            <div className="h-5 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-5/6"></div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export default Index;
